import React, { useEffect, useState } from 'react';
import leftArrow from '../../assets/img/white-left-icon.svg';
import Header from '../../components/Header';
import { useNavigate } from 'react-router-dom';
import '../../assets/css/analytics.css';
import { useAuth } from 'contexts/AuthContext';
import APIServices from 'services/APIServices';
import { selectLoader } from 'utils/Helpers';

const defaultProjectStatus = {
  totalProjects: 0,
  statusCounts: { ontime: 0, late: 0, early: 0 }
};

const defaultPendingItems = {
  signOff: 0,
  changeOrder: 0,
  changeRequest: 0
};

const defaultCompletions = {
  totalProjects: 0,
  projectCounts: { tillToday: 0 },
  milestoneCounts: { tillToday: 0 }
};

const Analytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (user?._id) {
      fetchDetails(user._id);
    }
  }, [user]);

  const [projectStatus, setProjectStatus] = useState(defaultProjectStatus);

  const [pendingItems, setPendingItems] = useState(defaultPendingItems);

  const [completions, setCompletions] = useState(defaultCompletions);

  const [selectedTimeFrame, setSelectedTimeFrame] = useState('Day');

  const fetchDetails = async (userId) => {
    setPageLoading(true);
    setLoadError(false);

    try {
      const [projectStatus, pendingItemsData, projectCompletions] = await Promise.all([
        APIServices.get(`/project/user/status/count/${userId}`),
        APIServices.get(`/project-requests/user/${userId}/project-requests-count`),
        APIServices.get(`/project/user/${userId}/completed-projects-count`)
      ]);

      setProjectStatus({
        ...defaultProjectStatus,
        ...projectStatus,
        statusCounts: {
          ...defaultProjectStatus.statusCounts,
          ...projectStatus?.statusCounts
        }
      });
      setPendingItems({
        ...defaultPendingItems,
        ...pendingItemsData?.counts
      });
      setCompletions({
        ...defaultCompletions,
        ...projectCompletions,
        projectCounts: {
          ...defaultCompletions.projectCounts,
          ...projectCompletions?.projectCounts
        },
        milestoneCounts: {
          ...defaultCompletions.milestoneCounts,
          ...projectCompletions?.milestoneCounts
        }
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setLoadError(true);
    } finally {
      setPageLoading(false);
    }
  };

  // Map selected timeframe to API response keys
  const timeFrameMapping = {
    Day: 'tillToday',
    Week: 'tillLastWeek',
    Quarter: 'tillLastQuarter',
    Year: 'tillLastYear'
  };
  const HeaderContent = (
    <>
      <h2>Analytics</h2>
      <div className="head-right">
        <a onClick={() => navigate(-1)}>
          <img src={leftArrow} alt="comp1" className="left-arrow" />
        </a>
      </div>
    </>
  );

  return (
    <div className="wrapper create_project">
      <Header content={HeaderContent} />
      <div className="main-content">
        {pageLoading ? (
          <div className="nodata-loader">{selectLoader(70)}</div>
        ) : loadError ? (
          <div className="nodata-loader">
            <p>Failed to load analytics.</p>
            <button className="btn btn-black" onClick={() => fetchDetails(user._id)}>
              Retry
            </button>
          </div>
        ) : (
          <div className="analytics-sec">
            <div className="container">
              <div className="row-cover row">
                {/* Project Status */}
                <div className="col-lg-6 col-md-6">
                  <div className="cardbg-white">
                    <div className="card-top-header">
                      <label>Project Status</label>
                    </div>
                    <div className="row project-status-row">
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg total-projects">
                          <p>Total Projects</p>
                          <span>
                            {projectStatus.totalProjects} <sub>(100%)</sub>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg on-time">
                          <p>On-time</p>
                          <span>
                            {projectStatus.statusCounts.ontime}{' '}
                            <sub>
                              (
                              {projectStatus.totalProjects
                                ? ((projectStatus.statusCounts.ontime / projectStatus.totalProjects) * 100).toFixed(1)
                                : 0}
                              %)
                            </sub>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg late">
                          <p>Late</p>
                          <span>
                            {projectStatus.statusCounts.late}{' '}
                            <sub>
                              (
                              {projectStatus.totalProjects
                                ? ((projectStatus.statusCounts.late / projectStatus.totalProjects) * 100).toFixed(1)
                                : 0}
                              %)
                            </sub>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg early">
                          <p>Early</p>
                          <span>
                            {projectStatus.statusCounts.early}{' '}
                            <sub>
                              (
                              {projectStatus.totalProjects
                                ? ((projectStatus.statusCounts.early / projectStatus.totalProjects) * 100).toFixed(1)
                                : 0}
                              %)
                            </sub>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pending Items */}
                <div className="col-lg-6 col-md-6">
                  <div className="cardbg-white">
                    <div className="card-top-header">
                      <label>Pending Items</label>
                    </div>
                    <div className="row project-status-row">
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg card-box-bodr">
                          <p>Change Orders</p>
                          <span>{pendingItems.changeOrder || 0}</span>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg card-box-bodr">
                          <p>Requests</p>
                          <span>{pendingItems.changeRequest || 0}</span>
                        </div>
                      </div>
                      <div className="col-lg-12 col-md-12">
                        <div className="card-boxBg card-box-bodr">
                          <p>Client Sign-off</p>
                          <span>{pendingItems.signOff || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Completions */}
                <div className="col-lg-6 col-md-6">
                  <div className="cardbg-white">
                    <div className="card-top-header">
                      <label>Completions</label>
                      <div className="month-cover">
                        {['Day', 'Week', 'Quarter', 'Year'].map((timeframe) => (
                          <button
                            key={timeframe}
                            className={`month-btn ${selectedTimeFrame === timeframe ? 'active' : ''}`}
                            onClick={() => setSelectedTimeFrame(timeframe)}>
                            {timeframe}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="row project-status-row">
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg card-box-bodr">
                          <p>Total Projects Completed</p>
                          <span>{completions.projectCounts[timeFrameMapping[selectedTimeFrame]]}</span>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="card-boxBg card-box-bodr">
                          <p>Total Milestones Completed</p>
                          <span>{completions.milestoneCounts[timeFrameMapping[selectedTimeFrame]]}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
